import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeoJSON, MapContainer, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { SubParcelDto } from '@forest/contracts';
import { Dialog } from '@/components/ui/Dialog';
import { SafeMap } from '@/components/ui/SafeMap';
import { Spinner } from '@/components/ui/Spinner';
import { MapLayerControl } from '@/components/map/MapLayerControl';
import { useUnattachedSubParcels } from './useSubParcels';
import { SUBPARCEL_FILL, SUBPARCEL_STROKE } from './SubParcelsMapLayer';

function FitTo({ geometry }: { geometry: GeoJSON.Geometry }) {
  const map = useMap();
  useEffect(() => {
    const b = L.geoJSON(geometry).getBounds();
    if (b.isValid()) map.fitBounds(b, { padding: [24, 24], maxZoom: 17 });
  }, [geometry, map]);
  return null;
}

/**
 * Subpolygons that lie in no polygon: receptions surveyed where no parcel
 * has been imported yet. Listed for review; they attach automatically when
 * their polygon arrives and the reception archive is imported again.
 */
export function UnattachedSubParcelsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation(['database', 'common']);
  const { data, isLoading } = useUnattachedSubParcels(open);
  const [selected, setSelected] = useState<SubParcelDto | null>(null);
  const items = data?.items ?? [];
  const current = selected ?? items[0] ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="xl"
      title={t('database:subparcels.unattachedTitle', {
        defaultValue: 'Subpoligoane fără poligon ({{count}})',
        count: items.length,
      })}
    >
      {isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
          <Spinner /> {t('common:state.loading', { defaultValue: 'Se încarcă…' })}
        </div>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500">
          {t('database:subparcels.unattachedEmpty', { defaultValue: 'Toate subpoligoanele au poligon.' })}
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            {t('database:subparcels.unattachedHint', {
              defaultValue:
                'Aceste subpoligoane nu se află în niciun poligon importat. După importul poligonului, reimportați arhiva recepției și subpoligonul se atașează automat.',
            })}
          </p>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
            <div className="max-h-[480px] overflow-auto rounded border border-slate-200 dark:border-slate-700">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500 dark:bg-slate-800">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">UCF</th>
                    <th className="px-2 py-1.5 text-left font-semibold">
                      {t('database:subparcels.primarie', { defaultValue: 'Primăria' })}
                    </th>
                    <th className="px-2 py-1.5 text-left font-semibold">
                      {t('database:subparcels.campaign', { defaultValue: 'Recepție' })}
                    </th>
                    <th className="px-2 py-1.5 text-right font-semibold">ha</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr
                      key={s.id}
                      onClick={() => setSelected(s)}
                      className={
                        'cursor-pointer border-t border-slate-100 dark:border-slate-800 ' +
                        (current?.id === s.id ? 'bg-orange-50 dark:bg-orange-900/20' : 'hover:bg-slate-50')
                      }
                    >
                      <td className="px-2 py-1.5 font-mono font-semibold" style={{ color: SUBPARCEL_STROKE }}>
                        {s.ucf}
                      </td>
                      <td className="px-2 py-1.5">
                        {s.primarie ?? '—'}
                        {s.raion ? <span className="text-slate-400"> · {s.raion}</span> : null}
                      </td>
                      <td className="px-2 py-1.5 text-slate-600">
                        {s.sezon && s.anul
                          ? `${t(`database:subparcels.sezon.${s.sezon}`, {
                              defaultValue: s.sezon === 'PRIMAVARA' ? 'Primăvara' : 'Toamna',
                            })} ${s.anul}`
                          : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{s.suprafata.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {current ? (
              <SafeMap height={480} className="overflow-hidden rounded border border-slate-200">
                <MapContainer center={[47.0105, 28.8638]} zoom={8} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                  <MapLayerControl />
                  <GeoJSON
                    key={current.id}
                    data={current.geometry as GeoJSON.Geometry}
                    style={() => ({ color: SUBPARCEL_STROKE, weight: 3, fillColor: SUBPARCEL_FILL, fillOpacity: 0.35 })}
                  />
                  <FitTo geometry={current.geometry as GeoJSON.Geometry} />
                </MapContainer>
              </SafeMap>
            ) : null}
          </div>
        </div>
      )}
    </Dialog>
  );
}
